import React, { useEffect, useRef, useState } from 'react';

// Animate a numeric value smoothly between renders. Apple's signature
// pattern for stat counters (Activity rings, Health, etc.) — when a
// streak ticks from 6 → 7 the digit is interpolated rather than
// snap-replaced, so the eye registers a "completion" beat.
//
// Usage: <TweenNumber value={streak} /> or  <TweenNumber value={total} duration={400} />
//
// The first mount is NOT animated (no eye-tracked event has occurred
// yet) — only subsequent value changes interpolate. Honours
// prefers-reduced-motion: a true value just snaps without animation.

function prefersReduced() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function TweenNumber({
  value,
  duration = 600,
  format = (n) => Math.round(n).toLocaleString(),
}) {
  const target = Number.isFinite(value) ? value : 0;
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef(0);
  const rafRef = useRef(0);
  // The first prop change after mount is almost always "fetch
  // resolved" (DEFAULT_PROGRESS streak=0 → real streak=N). Tweening
  // 0 → N would feel like a fake celebration the user didn't earn,
  // so we treat the first non-equal commit as "set without
  // animation" by syncing display to it before the animation effect
  // runs. From the second change onward we tween normally.
  const primedRef = useRef(false);

  useEffect(() => {
    if (!primedRef.current && display !== target) {
      primedRef.current = true;
      setDisplay(target);
      fromRef.current = target;
      return;
    }
    if (display === target) return;
    if (prefersReduced()) {
      setDisplay(target);
      return;
    }
    fromRef.current = display;
    startRef.current = performance.now();
    cancelAnimationFrame(rafRef.current);

    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / duration);
      // ease-out-cubic — fast at the start, settles into place.
      const eased = 1 - Math.pow(1 - t, 3);
      const v = fromRef.current + (target - fromRef.current) * eased;
      setDisplay(t === 1 ? target : v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // We deliberately depend only on `target`. Including `display`
    // would re-start the animation on every interpolation tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  // <span> with tabular-nums so the digit columns are fixed-width.
  // The base CSS sets font-variant-numeric: tabular-nums on <html>,
  // but specifying it here too survives any element-level override.
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
      {format(display)}
    </span>
  );
}
